// The director's script (.md) as the scene editor sees it. The prompt's
// "anatomy of a successful script" is one `###` heading per scene —
// `### 0:00–0:02 — Title card` — followed by its bullets (Visual, On-screen,
// VO). Lives in protocol/ because the shape is a contract between the director
// prompt and the browser; nothing inside a scene body is interpreted.

export interface Scene {
  /** "m:ss", null when the heading carries no time span. */
  start: string | null;
  end: string | null;
  title: string;
  /** The lines under the heading, verbatim, trimmed. */
  body: string;
}

export interface Script {
  /** Everything before the first scene heading (the `#` title, any prose), trimmed. */
  head: string;
  scenes: Scene[];
}

const HEADING = /^###\s+(.*)$/;
// "0:00–0:02 — Title card" (also "0:02.5"); any dash between the times and before the title.
const TIMED = /^(\d+:\d{2}(?:\.\d+)?)\s*[–—-]\s*(\d+:\d{2}(?:\.\d+)?)\s*[–—-]\s*(.+)$/;

export function parseScript(md: string): Script {
  const lines = md.split(/\r?\n/);
  const script: Script = { head: "", scenes: [] };
  let current: Scene | null = null;
  let buffer: string[] = [];
  const flush = () => {
    const text = buffer.join("\n").trim();
    if (current) current.body = text;
    else script.head = text;
    buffer = [];
  };
  for (const line of lines) {
    const heading = HEADING.exec(line);
    if (!heading) {
      buffer.push(line);
      continue;
    }
    flush();
    const text = (heading[1] ?? "").trim();
    const timed = TIMED.exec(text);
    current = {
      start: timed?.[1] ?? null,
      end: timed?.[2] ?? null,
      title: (timed?.[3] ?? text).trim(),
      body: "",
    };
    script.scenes.push(current);
  }
  flush();
  return script;
}

export function serializeScript(script: Script): string {
  const blocks = script.scenes.map((s) => {
    const heading = s.start && s.end ? `${s.start}–${s.end} — ${s.title}` : s.title;
    return s.body ? `### ${heading}\n${s.body}` : `### ${heading}`;
  });
  if (script.head) blocks.unshift(script.head);
  return `${blocks.join("\n\n")}\n`;
}

/** End of the last timed scene in whole seconds, null when no scene is timed. */
export function scriptSeconds(script: Script): number | null {
  for (let i = script.scenes.length - 1; i >= 0; i--) {
    const end = script.scenes[i]?.end;
    if (end) {
      const [m = 0, s = 0] = end.split(":").map(Number);
      return Math.round(m * 60 + s);
    }
  }
  return null;
}
